import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser, authApi } from './api/auth';
import { hasClerkSession } from './lib/clerkSso';
import type { UserRole } from './types';

// Pages publiques
import LoginPage from './pages/auth/LoginPage';
import OnboardingPage from './pages/auth/OnboardingPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Pages titulaire
import DashboardPage from './pages/titulaire/DashboardPage';
import PlanningPage from './pages/titulaire/PlanningPage';
import EmployeesPage from './pages/titulaire/EmployeesPage';
import EmployeeDetailPage from './pages/titulaire/EmployeeDetailPage';
import RequestsPage from './pages/titulaire/RequestsPage';
import AppointmentsPage from './pages/titulaire/AppointmentsPage';
import ShuttlePage from './pages/titulaire/ShuttlePage';
import ShuttleHistoryPage from './pages/titulaire/ShuttleHistoryPage';
import HoursBalancePage from './pages/titulaire/HoursBalancePage';
import ValidationPage from './pages/titulaire/ValidationPage';
import OrganizationPage from './pages/titulaire/OrganizationPage';
import SettingsPage from './pages/titulaire/SettingsPage';
import SimulationPage from './pages/titulaire/SimulationPage';
import MultiSitePage from './pages/titulaire/MultiSitePage';
import SocialListPage from './pages/titulaire/social/SocialListPage';
import SocialFormPage from './pages/titulaire/social/SocialFormPage';

// Pages salarié
import MyWeekPage from './pages/salarie/MyWeekPage';
import MyAvailabilityPage from './pages/salarie/MyAvailabilityPage';
import MyHistoryPage from './pages/salarie/MyHistoryPage';

// Pages équipe
import TeamWorkstationPage from './pages/team/TeamWorkstationPage';

// Équipe PharmaGestion et commerciaux (LOT_ROLES_HUB / LOT_DEMO)
import ConsolePage from './pages/platform/ConsolePage';
import DemoLaunchPage from './pages/platform/DemoLaunchPage';
import { estEquipe, type EtatPlateforme } from './api/platform';

// Layouts
import TitulaireLayout from './components/layout/TitulaireLayout';
import SalarieLayout from './components/layout/SalarieLayout';
import CounterLayout from './components/layout/CounterLayout';

function LoadingScreen() {
  return (
    <div className="w-full h-screen flex items-center justify-center bg-slate-100">
      <div className="text-sm text-slate-500">Chargement…</div>
    </div>
  );
}

/**
 * Session PharmaGestion valide, mais aucun compte Officina derrière.
 *
 * Deux cas, et il ne faut surtout pas les confondre :
 *  - le titulaire vient de souscrire au module RH → il n'a pas encore de
 *    pharmacie, on lui affiche le formulaire de création ;
 *  - son adresse ne correspond à rien → l'écran de connexion le lui dit.
 *
 * Sans ça, les deux se soldaient par une page blanche.
 */
function useClerkOnboarding() {
  const viaClerk = hasClerkSession();
  return useQuery({
    queryKey: ['auth', 'onboarding'],
    queryFn: () => authApi.onboardingStatus(),
    enabled: viaClerk,
    retry: false,
    staleTime: 30 * 1000,
  });
}

/**
 * LOT_ROLES_HUB / LOT_DEMO — une identité du hub sans compte Officina à servir :
 * l'équipe hors intervention va à la console, le commercial sans démo ouverte à
 * l'écran de lancement. `null` = pas concerné.
 */
function ecranPlateforme(platform: EtatPlateforme | null | undefined): React.ReactNode | null {
  if (!platform) return null;
  if (platform.role === 'COMMERCIAL') return <DemoLaunchPage />;
  if (estEquipe(platform.role)) return <Navigate to="/console" replace />;
  return null;
}

/** La console : équipe PharmaGestion seulement. */
function ConsoleRoute() {
  const onboarding = useClerkOnboarding();
  if (onboarding.isLoading) return <LoadingScreen />;
  if (!estEquipe(onboarding.data?.platform?.role)) return <Navigate to="/" replace />;
  return <ConsolePage />;
}

function PrivateRoute({ children, roles }: { children: React.ReactNode; roles: UserRole[] }) {
  const { data: user, isLoading } = useCurrentUser();
  const onboarding = useClerkOnboarding();
  const location = useLocation();

  if (isLoading || (hasClerkSession() && !user && onboarding.isLoading)) {
    return <LoadingScreen />;
  }
  if (!user && onboarding.data?.needsOnboarding) {
    return <OnboardingPage email={onboarding.data.email} />;
  }
  if (!user && onboarding.data?.platform) return <>{ecranPlateforme(onboarding.data.platform)}</>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!roles.includes(user.role)) {
    // Redirige selon le rôle effectif
    if (user.role === 'TITULAIRE') return <Navigate to="/dashboard" replace />;
    if (user.role === 'SALARIE') return <Navigate to="/my/week" replace />;
    if (user.role === 'TEAM_PIN') return <Navigate to="/planning" replace />;
  }

  return <>{children}</>;
}

/**
 * Le poste comptoir et le titulaire regardent LES MÊMES écrans — planning,
 * organisation, rendez-vous — mais pas dans le même cadre : l'un a trois
 * onglets et une horloge, l'autre toute la barre latérale de gestion.
 *
 * Les chemins, eux, sont identiques à dessein : déverrouiller avec le PIN ne
 * change pas de page, l'écran s'ouvre simplement autour.
 */
function SharedLayout() {
  const { data: user } = useCurrentUser();
  return user?.role === 'TEAM_PIN' ? <CounterLayout /> : <TitulaireLayout />;
}

function RootRedirect() {
  const { data: user, isLoading } = useCurrentUser();
  const onboarding = useClerkOnboarding();
  if (isLoading || (hasClerkSession() && !user && onboarding.isLoading)) {
    return <LoadingScreen />;
  }
  // Premier passage d'un titulaire venu du hub : sa pharmacie n'existe pas
  // encore, c'est le seul écran qui ait du sens.
  if (!user && onboarding.data?.needsOnboarding) {
    return <OnboardingPage email={onboarding.data.email} />;
  }
  if (!user && onboarding.data?.platform) return <>{ecranPlateforme(onboarding.data.platform)}</>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'TITULAIRE') return <Navigate to="/dashboard" replace />;
  if (user.role === 'SALARIE') return <Navigate to="/my/week" replace />;
  if (user.role === 'TEAM_PIN') return <Navigate to="/planning" replace />;
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/console" element={<ConsoleRoute />} />

      {/* Écrans partagés — titulaire ET poste comptoir (celui-ci en lecture
          seule, sauf la prise de rendez-vous). Mêmes chemins, cadre différent. */}
      <Route
        element={
          <PrivateRoute roles={['TITULAIRE', 'TEAM_PIN']}>
            <SharedLayout />
          </PrivateRoute>
        }
      >
        <Route path="/planning" element={<PlanningPage />} />
        <Route path="/organization" element={<OrganizationPage />} />
        <Route path="/appointments" element={<AppointmentsPage />} />
      </Route>

      {/* Titulaire — la gestion, qui ne regarde que lui */}
      <Route
        element={
          <PrivateRoute roles={['TITULAIRE']}>
            <TitulaireLayout />
          </PrivateRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/employees/:id" element={<EmployeeDetailPage />} />
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/shuttle" element={<ShuttlePage />} />
        <Route path="/shuttle/history" element={<ShuttleHistoryPage />} />
        <Route path="/hours-balance" element={<HoursBalancePage />} />
        <Route path="/validation" element={<ValidationPage />} />
        <Route path="/simulation" element={<SimulationPage />} />
        {/* Vue d'ensemble multi-site (M9-3, lot 3C) — accessible par le sélecteur en bas de la barre latérale. */}
        <Route path="/multi-site" element={<MultiSitePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* Option payante, activée par l'équipe PharmaGestion depuis la console. */}
        <Route path="/gestion-sociale" element={<SocialListPage />} />
        <Route path="/gestion-sociale/:id" element={<SocialFormPage />} />
      </Route>

      {/* Salarié */}
      <Route
        element={
          <PrivateRoute roles={['SALARIE']}>
            <SalarieLayout />
          </PrivateRoute>
        }
      >
        <Route path="/my/week" element={<MyWeekPage />} />
        <Route path="/my/availability" element={<MyAvailabilityPage />} />
        <Route path="/my/history" element={<MyHistoryPage />} />
      </Route>

      {/* Équipe (mode poste partagé) */}
      <Route
        path="/team"
        element={
          <PrivateRoute roles={['TEAM_PIN']}>
            <TeamWorkstationPage />
          </PrivateRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
